import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  TableContainer,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Card,
  CardContent,
  CardActionArea,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import axios from '../utils/axios';
import { useNotify } from '../hooks';
import { useIsMobile } from '../utils/responsive';
import { pickProcessTypeHref, productionProductLines, readStoredProcessTypeHref, writeStoredProcessTypeHref } from './productionUi.mjs';

export default function Production() {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const { success: showSuccess, error: showError } = useNotify();

  const [barcode, setBarcode] = useState('');
  const [types, setTypes] = useState([]);
  const [selectedTypeHref, setSelectedTypeHref] = useState('');
  const [tasks, setTasks] = useState([]);
  const [view, setView] = useState('list'); // 'list' | 'details'
  const [currentTask, setCurrentTask] = useState(null);
  const [taskDetails, setTaskDetails] = useState({ stages: [], products: [], loading: false });
  const [confirm, setConfirm] = useState({ open: false, stageId: null, productName: '', productId: null, quantity: 1 });
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [materialsOpen, setMaterialsOpen] = useState(false);
  const [materialsLoading, setMaterialsLoading] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [materialsMeta, setMaterialsMeta] = useState({ ok: true, quantity: 1 });
  const handleInputChange = (e) => setBarcode(e.target.value);

  // Типы производства
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get('/ms/process-types');
        const list = data?.types || [];
        setTypes(list);
        setSelectedTypeHref((current) => pickProcessTypeHref(list, {
          storedHref: readStoredProcessTypeHref(),
          taskHref: current,
        }) || current);
      } catch (_) {}
    })();
  }, []);

  const fetchTasks = async () => {
    if (!selectedTypeHref) return;
    try {
      setLoading(true);
      const { data } = await axios.get('/ms/production-tasks/list', { params: { typeHref: selectedTypeHref } });
      setTasks(Array.isArray(data?.tasks) ? data.tasks : []);
    } catch (_) { setTasks([]); }
    finally { setLoading(false); }
  };

  const openConfirm = (stageId, productName, productId) => setConfirm({ open: true, stageId, productName: productName || '', productId: productId || null, quantity: 1 });
  const closeConfirm = () => setConfirm({ open: false, stageId: null, productName: '', productId: null, quantity: 1 });

  const openTask = async (task) => {
    setCurrentTask(task);
    setView('details');
    try {
      const params = new URLSearchParams(location.search);
      params.set('task', task.id);
      navigate({ search: params.toString() }, { replace: false });
    } catch (_) {}
    await reloadDetails(task.id);
  };

  const reloadDetails = async (taskId) => {
    try {
      setTaskDetails(s => ({ ...s, loading: true }));
      const { data } = await axios.get(`/ms/production-tasks/${taskId}/details`);
      const stages = (data?.stages || []).filter(s => Number(s.totalQuantity) !== Number(s.completedQuantity));
      setTaskDetails({ stages, products: data?.products || [], loading: false });
      if (data?.processTypeHref) {
        setSelectedTypeHref(data.processTypeHref);
        writeStoredProcessTypeHref(data.processTypeHref);
      }
    } catch (_) {
      setTaskDetails({ stages: [], products: [], loading: false });
    }
  };

  const onCheckMaterials = async (stageRow, productRow) => {
    try {
      if (!productRow?.planId) return;
      setMaterialsOpen(true);
      setMaterialsLoading(true);
      setMaterials([]);
      const qty = Math.max(1, Number(stageRow.totalQuantity || 0) - Number(stageRow.completedQuantity || 0));
      const qp = new URLSearchParams({ planId: productRow.planId, quantity: String(qty || 1) }).toString();
      const { data } = await axios.get(`/ms/vacuum/check-materials?${qp}`);
      const list = Array.isArray(data?.items) ? data.items : [];
      setMaterials(list);
      setMaterialsMeta({ ok: Boolean(data?.ok), quantity: Number(data?.quantity || qty) });
    } catch (e) {
      setMaterials([]);
      setMaterialsMeta({ ok: false, quantity: 1 });
      setMaterialsOpen(true);
      showError(e?.response?.data?.message || 'Ошибка проверки материалов');
    } finally {
      setMaterialsLoading(false);
    }
  };

  const backToList = async () => {
    setView('list');
    setCurrentTask(null);
    setTaskDetails({ stages: [], products: [], loading: false });
    try {
      const params = new URLSearchParams(location.search);
      params.delete('task');
      navigate({ search: params.toString() }, { replace: false });
    } catch (_) {}
    await fetchTasks();
  };

  const completeStage = async (stageId) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const typeId = (types.find(t => t.href === selectedTypeHref)?.id) || '';
      const payload = { stageId, processTypeId: typeId, quantity: Math.max(1, Number(confirm.quantity || 1)), productId: confirm.productId || undefined };
      await axios.post('/ms/complete-stage', payload);
      showSuccess('Этап выполнен');
      if (currentTask?.id) await reloadDetails(currentTask.id);
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || 'Ошибка выполнения этапа';
      showError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Сканирование ШК
  useEffect(() => {
    let id;
    if (barcode.trim().length > 0) {
      id = setTimeout(async () => {
        try {
          const code = barcode.trim();
          const { data } = await axios.get('/ms/resolve-barcode', { params: { code } });
          const pid = data?.productId;
          if (pid) {
            if (view === 'details') {
              const productMap = new Map((taskDetails.products || []).map(p => [p.productId, p]));
              const p = productMap.get(pid);
              if (p) {
                const s = (taskDetails.stages || []).find(st => st.productionRowHref === p.productionRowHref && Number(st.totalQuantity) !== Number(st.completedQuantity));
                if (s && !submitting) { openConfirm(s.id, p.productName, p.productId); }
              }
            }
          }
        } catch (_) {}
        finally {
          setBarcode('');
        }
      }, 400);
    }
    return () => clearTimeout(id);
  }, [barcode, view, taskDetails]);

  // Авто-открытие задания по параметру ?task=
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const taskId = params.get('task');
    if (!taskId) return;
    if (view !== 'details' || String(currentTask?.id || '') !== String(taskId)) {
      setCurrentTask({ id: taskId });
      setView('details');
      reloadDetails(taskId);
    }
  }, [location.search]);


  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const d = new Date(String(dateStr).replace(' ', 'T'));
      if (isNaN(d.getTime())) return dateStr;
      const pad = (n) => n.toString().padStart(2, '0');
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch {
      return dateStr;
    }
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Производство
      </Typography>

      {/* Ввод штрихкода */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box component="form" onSubmit={(e) => e.preventDefault()} sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Сканируйте штрихкод"
            value={barcode}
            onChange={handleInputChange}
            autoFocus
            variant="outlined"
            size="small"
            sx={{ minWidth: 300 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Тип производства</InputLabel>
            <Select
              label="Тип производства"
              value={selectedTypeHref}
              onChange={(e) => {
                setSelectedTypeHref(e.target.value);
                writeStoredProcessTypeHref(e.target.value);
              }}
            >
              {!selectedTypeHref ? <MenuItem value="">Выберите тип</MenuItem> : null}
              {types.map(t => (<MenuItem key={t.id} value={t.href}>{t.name}</MenuItem>))}
            </Select>
          </FormControl>
          <Button variant="contained" onClick={fetchTasks} disabled={loading} startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
            {loading ? 'Загрузка…' : 'Получить задания'}
          </Button>
        </Box>
      </Paper>

      {/* Список заданий */}
      {view === 'list' && (
        isMobile ? (
          <Stack spacing={1.5}>
            {tasks.length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                {loading ? 'Загрузка...' : 'Нет заданий. Выберите тип и нажмите "Получить задания"'}
              </Paper>
            ) : (
              tasks.map((t) => (
                <Card key={t.id} variant="outlined">
                  <CardActionArea onClick={() => openTask(t)}>
                    <CardContent sx={{ pb: '12px !important', pt: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                        <Typography variant="subtitle1" fontWeight="bold">{t.name || t.id}</Typography>
                        <Typography variant="caption" color="text.secondary">{formatDate(t.moment)}</Typography>
                      </Box>
                      {t.description && (
                        <Typography variant="body2" color="text.secondary">
                          {t.description}
                        </Typography>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))
            )}
          </Stack>
        ) : (
          <Paper sx={{ p: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Задание</TableCell>
                    <TableCell>Дата</TableCell>
                    <TableCell>Комментарий</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tasks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        {loading ? 'Загрузка...' : 'Нет заданий. Выберите тип и нажмите "Получить задания"'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    tasks.map((t) => (
                      <TableRow key={t.id} hover sx={{ cursor: 'pointer' }} onClick={() => openTask(t)}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{t.name || t.id}</TableCell>
                        <TableCell>{formatDate(t.moment)}</TableCell>
                        <TableCell>{t.description || ''}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )
      )}

      {/* Детали задания */}
      {view === 'details' && (
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6" fontWeight="bold">{currentTask?.name || currentTask?.id}</Typography>
            <Button variant="outlined" onClick={backToList}>Назад</Button>
          </Box>
          <Typography variant="body2" color="text.secondary">Дата: {formatDate(currentTask?.moment)}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Комментарий: {currentTask?.description || '—'}</Typography>

          {taskDetails.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : taskDetails.stages.length === 0 ? (
            <Typography align="center" color="text.secondary" sx={{ py: 4 }}>Все этапы выполнены</Typography>
          ) : isMobile ? (
            <Stack spacing={1.5}>
              {taskDetails.stages.map((st) => {
                const prod = (taskDetails.products || []).find(p => p.productionRowHref === st.productionRowHref);
                const productLines = productionProductLines(prod);
                return (
                  <Card key={st.id} variant="outlined">
                    <CardContent sx={{ pb: '12px !important', pt: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                        <Box onClick={() => onCheckMaterials(st, prod)} sx={{ cursor: 'pointer', pr: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {productLines.name}
                          </Typography>
                          {productLines.color ? (
                            <Typography variant="body2" color="text.secondary">{productLines.color}</Typography>
                          ) : null}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {st.totalQuantity} / {st.completedQuantity}
                        </Typography>
                      </Box>
                      <Button
                        size="small"
                        variant="contained"
                        fullWidth
                        disabled={submitting}
                        onClick={() => openConfirm(st.id, prod?.productName, prod?.productId)}
                        sx={{ mt: 1 }}
                      >
                        Произвести
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Товар</TableCell>
                    <TableCell align="right">Всего / Выполнено</TableCell>
                    <TableCell align="right">Действие</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {taskDetails.stages.map((st) => {
                    const prod = (taskDetails.products || []).find(p => p.productionRowHref === st.productionRowHref);
                    const productLines = productionProductLines(prod);
                    return (
                      <TableRow key={st.id} hover>
                        <TableCell sx={{ cursor: 'pointer' }} onClick={() => onCheckMaterials(st, prod)}>
                          <Typography variant="body2">{productLines.name}</Typography>
                          {productLines.color ? (
                            <Typography variant="caption" color="text.secondary" display="block">{productLines.color}</Typography>
                          ) : null}
                        </TableCell>
                        <TableCell align="right">{`${st.totalQuantity} / ${st.completedQuantity}`}</TableCell>
                        <TableCell align="right">
                          <Button size="small" variant="contained" disabled={submitting} onClick={() => openConfirm(st.id, prod?.productName, prod?.productId)}>Произвести</Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Диалог подтверждения */}
      <Dialog open={confirm.open} onClose={submitting ? undefined : closeConfirm}>
        <DialogTitle>Выполнить этап?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>Товар: {confirm.productName || '—'}</Typography>
          <TextField
            sx={{ mt: 2 }}
            label="Количество"
            type="number"
            fullWidth
            value={confirm.quantity}
            onChange={(e) => setConfirm(s => ({ ...s, quantity: Math.max(1, Number(e.target.value || 1)) }))}
            inputProps={{ min: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm} disabled={submitting}>Нет</Button>
          <Button variant="contained" disabled={submitting} startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null} onClick={async () => { await completeStage(confirm.stageId); closeConfirm(); }}>{submitting ? 'Выполняем…' : 'Да'}</Button>
        </DialogActions>
      </Dialog>

      {/* Диалог материалов */}
      <Dialog open={materialsOpen} onClose={() => setMaterialsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Проверка материалов (qty: {materialsMeta.quantity})</DialogTitle>
        <DialogContent dividers>
          {materialsLoading ? (
            <Box sx={{ p: 1 }}><CircularProgress size={18} /></Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Материал</TableCell>
                  <TableCell align="right">Нужно</TableCell>
                  <TableCell align="right">Есть</TableCell>
                  <TableCell align="right">Дефицит</TableCell>
                  <TableCell>Группа</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {materials.map((m, idx) => (
                  <TableRow key={idx} sx={{ bgcolor: m.shortage > 0 ? 'error.lighter' : undefined }}>
                    <TableCell>{m.name}</TableCell>
                    <TableCell align="right">{m.need}</TableCell>
                    <TableCell align="right">{m.inStock}</TableCell>
                    <TableCell align="right" sx={{ color: m.shortage > 0 ? 'error.main' : undefined }}>{m.shortage}</TableCell>
                    <TableCell>{m.groupName || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color={materialsMeta.ok ? 'success.main' : 'error.main'}>
              {materialsMeta.ok ? 'Материалов достаточно' : 'Есть дефицит материалов'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMaterialsOpen(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
